import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Bookmark, X, ChevronLeft, ChevronRight, Trash2, Send, Flag, CirclePlus, MoreVertical } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { getMediaUrl } from '../../utils/mediaUrl';
import { shareContent, buildShareUrl } from '../../utils/shareContent';
import { photoTexts } from './photoTranslations';
import VerifiedBadge from '../VerifiedBadge';

// Fullscreen swipeable viewer (shared with Profile) — rendered in a portal so
// ancestor transforms/backdrop-filters can never trap the fixed overlay behind cards.
export const PhotoFullscreenViewer = ({ images, startIndex = 0, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const total = images.length;
  return createPortal(
    <div className="fixed inset-0 bg-black z-[10000] flex items-center justify-center" data-testid="photo-fullscreen-viewer" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full z-10" data-testid="photo-fullscreen-close">
        <X className="w-6 h-6 text-white" />
      </button>
      {total > 1 && index > 0 && (
        <button onClick={(e) => { e.stopPropagation(); setIndex(index - 1); }} className="absolute left-3 p-2 bg-white/10 rounded-full z-10" data-testid="photo-fullscreen-prev">
          <ChevronLeft className="w-7 h-7 text-white" />
        </button>
      )}
      {total > 1 && index < total - 1 && (
        <button onClick={(e) => { e.stopPropagation(); setIndex(index + 1); }} className="absolute right-3 p-2 bg-white/10 rounded-full z-10" data-testid="photo-fullscreen-next">
          <ChevronRight className="w-7 h-7 text-white" />
        </button>
      )}
      <img
        src={getMediaUrl(images[index])}
        alt={`photo-${index + 1}`}
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {total > 1 && (
        <div className="absolute bottom-6 flex gap-1.5">
          {images.map((_, i) => (
            <span key={i} className={`w-2 h-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/40'}`} />
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

const PhotoCommentsModal = ({ photoId, onClose, onCommentAdded }) => {
  const { language } = useLanguage();
  const P = photoTexts(language);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`/api/photos/${photoId}/comments`)
      .then((r) => setComments(r.data.comments || []))
      .catch(() => {});
  }, [photoId]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await axios.post(`/api/photos/${photoId}/comments`, { content: t });
      setComments((prev) => [r.data, ...prev]);
      setText('');
      onCommentAdded?.();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center" onClick={onClose} data-testid="photo-comments-modal">
      <div className="bg-gray-900 border border-white/10 rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[75vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="font-bold text-white">{P.comments}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="photo-comments-close">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {comments.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-6">{P.noComments}</p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="bg-white/5 rounded-xl p-3" data-testid={`photo-comment-${c.id}`}>
                <p className="text-xs text-fuchsia-300 font-semibold mb-1">{c.user?.name || c.user?.username || 'Utilisateur'}</p>
                <p className="text-sm text-gray-200 break-words">{c.content}</p>
              </div>
            ))
          )}
        </div>
        <div className="p-3 border-t border-white/10 flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder={P.addComment}
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-fuchsia-500/60"
            data-testid="photo-comment-input"
          />
          <button onClick={send} disabled={sending || !text.trim()} className="p-2.5 bg-fuchsia-600 rounded-full disabled:opacity-40" data-testid="photo-comment-send">
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// Instagram-style photo post card for the home feed
export const PhotoPostCard = ({ post, onUpdate, showDelete = false }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const P = photoTexts(language);
  const navigate = useNavigate();
  const [liked, setLiked] = useState(!!post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [saved, setSaved] = useState(!!post.is_saved);
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [fullscreenIdx, setFullscreenIdx] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [addingStory, setAddingStory] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const scrollRef = useRef(null);
  const images = post.image_urls || [];
  const isOwner = user?.id === post.user_id;

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCarouselIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  const toggleLike = async () => {
    try {
      const r = await axios.post(`/api/photos/${post.id}/like`);
      setLiked(r.data.liked);
      setLikesCount((p) => (r.data.liked ? p + 1 : Math.max(0, p - 1)));
    } catch (e) { toast.error('Erreur'); }
  };

  const toggleSave = async () => {
    try {
      const r = await axios.post(`/api/photos/${post.id}/save`);
      setSaved(r.data.saved);
      toast.success(r.data.saved ? P.savedOk : P.removedSaved);
    } catch (e) { toast.error('Erreur'); }
  };

  const share = async () => {
    try {
      await shareContent({
        title: 'Destine',
        text: P.shareText,
        url: buildShareUrl('photo', post.id),
      });
      axios.post(`/api/photos/${post.id}/share`).catch(() => {});
    } catch (e) { /* cancelled */ }
  };

  const remove = async () => {
    if (!window.confirm(P.confirmDelete)) return;
    try {
      await axios.delete(`/api/photos/${post.id}`);
      toast.success(P.deleted);
      onUpdate?.();
    } catch (e) { toast.error('Erreur'); }
  };

  const reportPhoto = async () => {
    const reason = window.prompt(P.reportPrompt);
    if (!reason || !reason.trim()) return;
    try {
      const form = new FormData();
      form.append('photo_id', post.id);
      form.append('reason', reason.trim());
      await axios.post('/api/reports/photo', form);
      toast.success(P.reported);
    } catch (e) { toast.error('Erreur'); }
  };

  const addToStory = async () => {
    if (addingStory) return;
    setAddingStory(true);
    try {
      const url = getMediaUrl(images[carouselIdx] || images[0]);
      const blob = await fetch(url).then((r) => r.blob());
      const form = new FormData();
      form.append('media_type', 'image');
      form.append('media_file', blob, 'photo.jpg');
      form.append('linked_content_id', post.id);
      form.append('linked_content_type', 'photo');
      if (post.caption) form.append('text_overlay', post.caption.slice(0, 150));
      await axios.post('/api/stories', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(P.storyAdded);
    } catch (e) {
      console.error('add to story error:', e);
      toast.error(P.storyKo);
    } finally {
      setAddingStory(false);
    }
  };

  // Instagram-style caption: only the first line shows by default
  const captionLines = (post.caption || '').split('\n');
  const isLongCaption = captionLines.length > 1 || (post.caption || '').length > 90;

  return (
    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden" data-testid="photo-post-card">
      {/* Header */}
      <div className="p-3 flex items-center gap-3">
        <button
          onClick={() => navigate(`/profile/${post.user?.id || post.user_id}`)}
          className="w-10 h-10 bg-gradient-to-br from-pink-500 to-fuchsia-600 rounded-full flex items-center justify-center overflow-hidden shrink-0"
        >
          {post.user?.profile_image_url ? (
            <img src={getMediaUrl(post.user.profile_image_url)} alt={post.user?.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-bold text-white">{post.user?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <button onClick={() => navigate(`/profile/${post.user?.id || post.user_id}`)} className="flex items-center gap-1">
            <span className="font-semibold text-white truncate">{post.user?.name || 'Utilisateur'}</span>
            {post.user?.is_verified && <VerifiedBadge size={14} />}
          </button>
          <p className="text-xs text-gray-400 truncate">@{post.user?.username || 'user'}</p>
        </div>
        {isOwner && showDelete && (
          <button onClick={remove} className="p-2 text-gray-400 hover:text-red-400 transition-colors" data-testid="photo-post-delete">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        {/* 3-dots menu — report only (v50.10.1). Delete stays on the trash icon (own profile only). */}
        <div className="relative">
          <button onClick={() => setShowMenu((v) => !v)} className="p-2 text-gray-400 hover:text-white transition-colors" data-testid="photo-post-menu-btn">
            <MoreVertical className="w-5 h-5" />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-9 z-30 bg-gray-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[170px]" data-testid="photo-post-menu">
                <button
                  onClick={() => { if (isOwner) return; setShowMenu(false); reportPhoto(); }}
                  disabled={isOwner}
                  className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 ${isOwner ? 'text-gray-500 cursor-not-allowed opacity-60' : 'text-orange-300 hover:bg-white/10'}`}
                  data-testid="photo-post-menu-report"
                >
                  <Flag className="w-4 h-4" /> {P.report}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Photo carousel */}
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide items-center bg-black"
          style={{ scrollbarWidth: 'none' }}
        >
          {images.map((url, idx) => (
            <img
              key={idx}
              src={getMediaUrl(url)}
              alt={`photo ${idx + 1} ${P.photoOf} ${images.length}`}
              className="w-full h-auto max-h-[75vh] object-contain snap-center shrink-0 cursor-pointer"
              onClick={() => setFullscreenIdx(idx)}
              data-testid={`photo-post-img-${idx}`}
              loading="lazy"
            />
          ))}
        </div>
        {images.length > 1 && (
          <>
            <div className="absolute top-3 right-3 bg-black/60 rounded-full px-2.5 py-1 text-xs text-white font-semibold">
              {carouselIdx + 1}/{images.length}
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === carouselIdx ? 'bg-white' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-5">
        <button onClick={toggleLike} className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-pink-500' : 'text-gray-300 hover:text-pink-500'}`} data-testid="photo-post-like">
          <Heart className={`w-6 h-6 ${liked ? 'fill-current' : ''}`} />
          <span className="text-sm">{likesCount}</span>
        </button>
        <button onClick={() => setShowComments(true)} className="flex items-center gap-1.5 text-gray-300 hover:text-fuchsia-400 transition-colors" data-testid="photo-post-comments">
          <MessageCircle className="w-6 h-6" />
          <span className="text-sm">{commentsCount}</span>
        </button>
        <button onClick={share} className="text-gray-300 hover:text-blue-400 transition-colors" data-testid="photo-post-share">
          <Share2 className="w-6 h-6" />
        </button>
        <button onClick={addToStory} disabled={addingStory} className="text-gray-300 hover:text-emerald-400 transition-colors disabled:opacity-40" title={P.addToStory} data-testid="photo-post-add-story">
          <CirclePlus className="w-6 h-6" />
        </button>
        <button onClick={toggleSave} className={`ml-auto transition-colors ${saved ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`} title={saved ? P.saved : P.save} data-testid="photo-post-save">
          <Bookmark className={`w-6 h-6 ${saved ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Caption — 1 line by default, tap to expand (max 10 lines) */}
      {post.caption && (
        <div className="px-4 pb-3">
          <p className={`text-sm text-gray-200 whitespace-pre-wrap break-words ${showFullCaption ? '' : 'line-clamp-1'}`} data-testid="photo-post-caption">
            <span className="font-semibold text-white mr-2">{post.user?.username || post.user?.name}</span>
            {post.caption}
          </p>
          {isLongCaption && (
            <button
              onClick={() => setShowFullCaption((v) => !v)}
              className="text-xs text-gray-400 hover:text-gray-200 mt-0.5"
              data-testid="photo-caption-toggle"
            >
              {showFullCaption ? P.seeLess : P.seeMore}
            </button>
          )}
        </div>
      )}

      {showComments && (
        <PhotoCommentsModal
          photoId={post.id}
          onClose={() => setShowComments(false)}
          onCommentAdded={() => setCommentsCount((p) => p + 1)}
        />
      )}
      {fullscreenIdx !== null && (
        <PhotoFullscreenViewer images={images} startIndex={fullscreenIdx} onClose={() => setFullscreenIdx(null)} />
      )}
    </div>
  );
};

export default PhotoPostCard;
